import BaseController from "confinus_ui/base_controller";
// FormFieldController previously imported from mercor/forms/form_field_controller.
// After the DateCalendar rewrite to inherit Decor::Suite::Forms::TextField, the
// Mercor copy of the form_field controller was deleted. Used here as a structural
// type covering the surface this file actually touches on size/quantity field
// controllers (Decor Suite NumberField/Select).
type FormFieldController = {
  element: Element;
  valid: boolean;
  value: string;
  // Optional — Decor Select doesn't expose a writable `required` setter;
  // NumberField does. Guarded at call sites.
  required?: boolean;
};
import PurchaseConfiguratorExtraInfoController from "mercor/purchase_configurator/extra_info_controller";
import {
  calculateTotalPriceForDisplay,
  calculateTotalPriceNumber,
  isNullOrBigZero,
  parseVariantSize,
} from "lib/price_calculations";
import { getAppConfiguration } from "lib/app_configuration";
import { markAsSafeHTML, safelySetInnerHTML } from "lib/util/safe_html";
import { Mercor__PurchaseConfigurator__ExtraInfoControllerIdentifier } from "controllers/identifiers";
// NOTE: Migrated to Decor::Suite::Forms::NumberField/Select (strangler pattern).
import {
  DecorSuiteFormsNumberFieldIdentifier,
  DecorSuiteFormsSelectIdentifier,
} from "decor";
import { NumberFieldController } from "decor";
import { Controller } from "@hotwired/stimulus";

// Minimal duck-typed Decor::Suite Select surface used in this controller —
// only the input element id (via Stimulus `element`) is read here.
interface SuiteSelectController extends Controller {
  element: HTMLElement;
  valid: boolean;
  value: string;
}

type ProductPriceChangedDetail = {
  totalPriceNumber: number;
  sku: string;
  variant: string;
  size: string | undefined;
  parsedSize: Big.Big | undefined;
  validSize: boolean;
  hasSize: boolean;
};

export type ProductPriceChangedEvent = CustomEvent<ProductPriceChangedDetail>;

interface VariantDataAttributes {
  sku: string;
  variantEncodedId: string;
  fallbackDisplay: string;
  extraChargeMultiplier: string | number;
  priceMultiplier: string | number;
  quantityType: "float" | "integer";
  sizeUnitConversionFactor?: string | number;
  sizeExcludedFromCalculation?: boolean;
  currency: string;
}

export default class OrderGuideRowController extends BaseController {
  public static targets = [
    "shortDescription",
    "sku",
    "pricePerUom",
    "rowTotalPrice",
    "check",
    "warn",
  ];

  public static values = {
    variantsQuantityData: Object,
    variantEncodedId: String,
  };

  declare private readonly variantsQuantityDataValue: {
    [key: string]: VariantDataAttributes;
  };
  declare private readonly variantEncodedIdValue: string;

  declare private readonly shortDescriptionTarget: HTMLTableCellElement;
  declare private readonly hasShortDescriptionTarget: boolean;
  declare private readonly skuTarget: HTMLTableCellElement;
  declare private readonly hasSkuTarget: boolean;
  declare private readonly rowTotalPriceTarget: HTMLDivElement;
  declare private readonly checkTarget: HTMLElement;
  declare private readonly hasCheckTarget: boolean;
  declare private readonly warnTarget: HTMLElement;
  declare private readonly hasWarnTarget: boolean;

  public static outlets = [
    Mercor__PurchaseConfigurator__ExtraInfoControllerIdentifier,
    DecorSuiteFormsNumberFieldIdentifier,
    DecorSuiteFormsSelectIdentifier,
  ];
  declare private readonly mercorPurchaseConfiguratorExtraInfoOutlets: PurchaseConfiguratorExtraInfoController[];
  declare private readonly decorSuiteFormsNumberFieldOutlets: NumberFieldController[];
  declare private readonly decorSuiteFormsSelectOutlets: SuiteSelectController[];

  private get extraInfoControllers() {
    return this.mercorPurchaseConfiguratorExtraInfoOutlets;
  }

  declare protected quantityData: { [key: string]: VariantDataAttributes };

  onInitialize() {
    this.quantityData = this.variantsQuantityDataValue;
    return super.onInitialize();
  }

  public get variantEncodedId(): string {
    return this.variantEncodedIdValue;
  }

  private isCurrentConfigurationValid(hasRequiredSize: boolean) {
    const size = this.currentSizeField;
    const req = hasRequiredSize && this.currentQuantityField!.valid;
    return size ? size.valid && req : req;
  }

  public handlePortionSizeChanged(evt: Event) {
    const size = parseVariantSize(this.currentSize);
    if (size && size.gt(1000000)) {
      this.currentSizeField!.value = "1000000";
    }
    this.updateCalculations();
  }

  public handleQuantityChanged(evt: Event) {
    const qty = parseVariantSize(this.currentQuantity);
    if (qty && qty.gt(1000000)) {
      this.currentQuantityField!.value = "1000000";
    }
    this.updateCalculations();
  }

  private updateCalculations() {
    let fallbackDisplay = "-";

    try {
      const variant = this.variantEncodedIdValue;
      const sku = this.quantityData[variant].sku;

      const quantity = this.currentQuantity;
      const size = this.currentSize;
      const parsedSize = parseVariantSize(size);
      const validSize = !isNullOrBigZero(parsedSize);
      const hasSize = size != undefined;

      const {
        currency,
        extraChargeMultiplier,
        priceMultiplier,
        quantityType,
        sizeExcludedFromCalculation,
        sizeUnitConversionFactor,
        ...other
      } = this.quantityData[variant];

      fallbackDisplay = other.fallbackDisplay;

      const opts = {
        currency,
        extraChargeMultiplier,
        locale: getAppConfiguration("locale"),
        priceMultiplier,
        quantity,
        quantityType,
        size,
        sizeExcludedFromCalculation,
        sizeUnitConversionFactor,
      };
      const totalPriceNumber = calculateTotalPriceNumber(opts);
      const totalPrice = calculateTotalPriceForDisplay(opts, totalPriceNumber);

      this.updateRowTotal(totalPrice !== null ? totalPrice : fallbackDisplay);

      this.extraInfoControllers.forEach((c) =>
        c.updateValue({ quantity, quantityType, size, fallbackDisplay }),
      );

      this.checkMarks(
        totalPriceNumber > 0,
        this.isCurrentConfigurationValid(hasSize ? validSize : true),
      );

      const detail: ProductPriceChangedDetail = {
        totalPriceNumber,
        sku,
        variant,
        size,
        parsedSize,
        validSize,
        hasSize,
      };

      this.dispatch("product_price_change", {
        bubbles: true,
        cancelable: false,
        detail,
      });

      if (this.currentSizeField) {
        this.currentSizeField.required =
          hasSize && !validSize && totalPriceNumber > 0;
      }
    } catch (e) {
      console.error("Order guide calc error", e);
      this.updateRowTotal("!!");
    }
  }

  private updateRowTotal(formattedTotal: string) {
    safelySetInnerHTML(
      this.rowTotalPriceTarget,
      markAsSafeHTML(formattedTotal),
    );
  }

  private get currentQuantity(): string {
    return this.currentQuantityField?.value || "0";
  }

  private get currentQuantityField(): NumberFieldController | undefined {
    return this.decorSuiteFormsNumberFieldOutlets.find((field) =>
      field.element.id.includes("quantity"),
    );
  }

  private get currentSize(): string | undefined {
    const field = this.currentSizeField;
    return field ? field.value : undefined;
  }

  private get currentSizeField(): FormFieldController | undefined {
    let match: FormFieldController | undefined =
      this.decorSuiteFormsNumberFieldOutlets.find(
        (field) =>
          field.element.id.includes("portion-size") ||
          field.element.id.includes("portion_size"),
      );
    if (match) return match;

    return this.decorSuiteFormsSelectOutlets.find(
      (field) =>
        field.element.id.includes("portion-size") ||
        field.element.id.includes("portion_size"),
    );
  }

  public getState(): {
    sku: string;
    variant: string;
    quantity: string;
    size?: string;
  } | null {
    const quantity = this.currentQuantity;
    if (!quantity || quantity === "0" || quantity === "") return null;
    return {
      sku: this.sku,
      variant: this.variantEncodedIdValue,
      quantity,
      size: this.currentSize,
    };
  }

  public restoreState(state: { quantity: string; size?: string }): void {
    const qtyField = this.currentQuantityField;
    if (qtyField && state.quantity) qtyField.value = state.quantity;

    if (state.size) {
      const sizeField = this.currentSizeField;
      if (sizeField) sizeField.value = state.size;
    }

    this.updateCalculations();
  }

  public get sku(): string {
    const el = this.element as HTMLElement;
    return (el.getAttribute("data-product-sku") || "").toLowerCase();
  }

  private checkMarks(hasTotal: boolean, isValidConfiguration: boolean) {
    if (!this.hasCheckTarget || !this.hasWarnTarget) return;

    if (isValidConfiguration) {
      this.checkTarget.style.display = hasTotal ? "block" : "none";
      this.warnTarget.style.display = "none";
    } else {
      this.checkTarget.style.display = "none";
      this.warnTarget.style.display = "block";
    }
  }
}
